import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  X,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Check,
  Loader2,
  ExternalLink,
  Flame,
} from 'lucide-react';
import {
  usePatients,
  useUpdateAnthropometrics,
  useGenerateDietPlan,
} from '../lib/queries';
import {
  ApiError,
  isPaymentRequired,
  limitReason,
  previewPlanTargets,
  type MealType,
  type ActivityLevel,
  type UpdateAnthropometricsBody,
  type GeneratePlanBody,
  type MacroInput,
  type PreviewTargetsResponse,
  type PatientGoalType,
  type DeficitOverride,
  type LimitReason,
  MEAL_TYPES,
} from '../lib/api';
import { PaywallNotice } from './PaywallNotice';
import { LimitReachedModal } from './LimitReachedModal';

// ─── Obiettivo (3 opzioni esposte in UI) ─────────────────────────────────────

type GoalChoice = 'weight' | 'performance' | 'mass';

const GOAL_OPTIONS: Array<{ value: GoalChoice; label: string; hint: string }> = [
  { value: 'weight', label: 'Dimagrimento', hint: '−500 kcal' },
  { value: 'performance', label: 'Mantenimento', hint: '±0 kcal' },
  { value: 'mass', label: 'Aumento massa', hint: '+300 kcal' },
];

/** Normalizza il goal del profilo (può essere null/'custom') in una delle 3 scelte UI. */
function normalizeGoal(raw: string | null | undefined): GoalChoice {
  if (raw === 'weight' || raw === 'mass' || raw === 'performance') return raw;
  return 'performance';
}

// ─── Costanti UI ─────────────────────────────────────────────────────────────

const ACTIVITY_LABELS: Record<ActivityLevel, string> = {
  sedentary: 'Sedentario (lavoro da scrivania, poco movimento)',
  lightly_active: 'Leggero (esercizio 1-3 giorni/sett)',
  moderately_active: 'Moderato (esercizio 3-5 giorni/sett)',
  very_active: 'Attivo (esercizio 6-7 giorni/sett)',
  extra_active: 'Molto attivo (atleta o lavoro fisico intenso)',
};

const ACTIVITY_OPTIONS: ActivityLevel[] = [
  'sedentary',
  'lightly_active',
  'moderately_active',
  'very_active',
  'extra_active',
];

type Step = 1 | 2 | 3;

const STEPS: Array<{ n: Step; label: string }> = [
  { n: 1, label: 'Paziente' },
  { n: 2, label: 'Dati clinici' },
  { n: 3, label: 'Parametri piano' },
];

// ─── Tipi locali ─────────────────────────────────────────────────────────────

interface AnthroForm {
  height_cm: string;
  weight_kg: string;
  birth_date: string;
  sex: 'M' | 'F' | '';
  activity_level: ActivityLevel | '';
  /** Migration 0039 — su 'simple' la dieta usa solo ingredienti comuni. Opzionale. */
  cooking_simplicity: CookingSimplicity | '';
  /** Migration 0039 — gusti / cibi non graditi. Opzionale. */
  food_preferences: string;
}

type CookingSimplicity = 'simple' | 'normal' | 'free';

const SIMPLICITY_OPTIONS: Array<{ value: CookingSimplicity; label: string; hint: string }> = [
  { value: 'simple', label: 'Semplice', hint: 'solo ingredienti comuni, preparazioni veloci' },
  { value: 'normal', label: 'Normale', hint: 'nessun vincolo particolare' },
  { value: 'free', label: 'Libero', hint: 'anche piatti elaborati' },
];

interface PlanForm {
  title: string;
  start_date: string;
  weeks: string;
  macro_mode: 'percentage' | 'grams_per_kg';
  protein_pct: string;
  fat_pct: string;
  carb_pct: string;
  protein_g_per_kg: string;
  fat_g_per_kg: string;
  active_meals: Set<MealType>;
  kcal_mode: 'auto' | 'manual';
  target_kcal_override: string;
  /** Modalità del deficit/surplus manuale (attivo in kcal_mode='auto'). */
  deficit_mode: 'kcal' | 'percent';
  /** Valore deficit/surplus (stringa editabile). Segno: negativo = deficit. */
  deficit_value: string;
}

interface GenerateResult {
  plan_id: string;
  meals_created: number;
  items_created: number;
  warnings: string[];
  target_kcal: number;
  macros: { protein_g: number; carb_g: number; fat_g: number };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function today(): string {
  return new Date().toISOString().split('T')[0];
}

function pctSum(p: string, f: string, c: string): number {
  return (parseFloat(p) || 0) + (parseFloat(f) || 0) + (parseFloat(c) || 0);
}

function emptyAnthro(): AnthroForm {
  return {
    height_cm: '', weight_kg: '', birth_date: '', sex: '', activity_level: '',
    cooking_simplicity: '', food_preferences: '',
  };
}

function emptyPlan(patientName?: string): PlanForm {
  const t = today();
  return {
    title: patientName ? `Piano AI ${patientName} - ${t}` : `Piano AI - ${t}`,
    start_date: t,
    weeks: '4',
    macro_mode: 'percentage',
    protein_pct: '30',
    fat_pct: '30',
    carb_pct: '40',
    protein_g_per_kg: '1.8',
    fat_g_per_kg: '1.0',
    active_meals: new Set<MealType>(['breakfast', 'snack_morning', 'lunch', 'snack_afternoon', 'dinner']),
    kcal_mode: 'auto',
    target_kcal_override: '',
    deficit_mode: 'kcal',
    deficit_value: '', // popolato dal preset obiettivo al primo preview (vedi goalDefaultDeficit)
  };
}

/**
 * Delta deficit/surplus di default per obiettivo, nella modalità data.
 * Usato per prefillare il campo quando si sceglie un obiettivo o si cambia modalità.
 *   weight (dimagrimento) → −500 kcal / −20%
 *   mass (massa)          → +300 kcal / +15%
 *   performance (mant.)   → 0
 */
function goalDefaultDeficit(goal: GoalChoice, mode: 'kcal' | 'percent'): string {
  if (goal === 'weight') return mode === 'percent' ? '-20' : '-500';
  if (goal === 'mass') return mode === 'percent' ? '15' : '300';
  return '0';
}

// ─── Stili condivisi (identici a PianoEditor) ────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid var(--line)',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 13,
  background: 'var(--surface)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  boxSizing: 'border-box',
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: 'var(--red)',
  background: 'var(--red-soft, #fff0f0)',
};

function FieldBlock({
  label,
  children,
  error,
}: {
  label: string;
  children: React.ReactNode;
  error?: string | null;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10.5,
          fontWeight: 700,
          color: error ? 'var(--red)' : 'var(--mute)',
          letterSpacing: 1.2,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      {children}
      {error && (
        <span style={{ fontSize: 11.5, color: 'var(--red)', lineHeight: 1.4 }}>{error}</span>
      )}
    </label>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  /** Se passato, pre-seleziona il paziente e salta lo step 1. */
  preselectedPatientId?: string | null;
  onClose: () => void;
}

// ─── Componente principale ────────────────────────────────────────────────────

export function GeneraDietaModal({ preselectedPatientId, onClose }: Props) {
  const navigate = useNavigate();

  // Step iniziale: se paziente pre-selezionato, parti dal 2
  const [step, setStep] = useState<Step>(preselectedPatientId ? 2 : 1);

  // Selezione paziente
  const [selectedPatientId, setSelectedPatientId] = useState<string>(preselectedPatientId || '');
  const [searchQ, setSearchQ] = useState('');

  // Form antropometrico
  const [anthro, setAnthro] = useState<AnthroForm>(emptyAnthro);
  const [anthroErrors, setAnthroErrors] = useState<Partial<Record<keyof AnthroForm, string>>>({});
  const [needsAnthro, setNeedsAnthro] = useState(false);

  // Form piano
  const [planForm, setPlanForm] = useState<PlanForm>(() => emptyPlan());

  // Errore globale
  const [error, setError] = useState<string | null>(null);
  const [paywallBlocked, setPaywallBlocked] = useState(false);
  // Sprint 10 Fase 3/F5 — 402 di enforcement (quota AI esaurita), distinto dal paywall generico.
  const [limitModal, setLimitModal] = useState<LimitReason | null>(null);

  // Risultato generazione
  const [result, setResult] = useState<GenerateResult | null>(null);

  // Preview targets (kcal/macro calcolate dal backend, no LLM)
  const [preview, setPreview] = useState<PreviewTargetsResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Obiettivo selezionato (default dal goal del profilo paziente, via primo preview)
  const [goal, setGoal] = useState<GoalChoice>('performance');
  const goalInitFromPreview = useRef(false);

  const { data: patients, isLoading: patientsLoading } = usePatients();
  const updateAnthroMutation = useUpdateAnthropometrics();
  const generateMutation = useGenerateDietPlan();

  // Quando il paziente viene selezionato, aggiorna titolo piano
  const selectedPatient = useMemo(
    () => (patients || []).find((p) => p.id === selectedPatientId) || null,
    [patients, selectedPatientId],
  );

  useEffect(() => {
    if (selectedPatient) {
      setPlanForm((f) => ({
        ...f,
        title: `Piano AI ${selectedPatient.name} - ${today()}`,
      }));
    }
  }, [selectedPatient]);

  // Filtra lista pazienti per ricerca
  const filteredPatients = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return patients || [];
    return (patients || []).filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.human_id && p.human_id.toLowerCase().includes(q)) ||
        (p.email && p.email.toLowerCase().includes(q)),
    );
  }, [patients, searchQ]);

  // ─── Validazione step 2 (antropometria) ────────────────────────────────────

  function validateAnthro(highlight?: keyof AnthroForm): boolean {
    const errs: Partial<Record<keyof AnthroForm, string>> = {};
    const h = parseFloat(anthro.height_cm);
    if (!anthro.height_cm || Number.isNaN(h) || h < 100 || h > 250) {
      errs.height_cm = 'Altezza non valida (100-250 cm)';
    }
    const w = parseFloat(anthro.weight_kg);
    if (!anthro.weight_kg || Number.isNaN(w) || w < 30 || w > 300) {
      errs.weight_kg = 'Peso non valido (30-300 kg)';
    }
    if (!anthro.birth_date) {
      errs.birth_date = 'Data di nascita obbligatoria';
    } else {
      const age = (Date.now() - new Date(anthro.birth_date).getTime()) / (1000 * 60 * 60 * 24 * 365.25);
      if (age < 10 || age > 120) errs.birth_date = 'Data di nascita non valida';
    }
    if (!anthro.sex) {
      errs.sex = 'Seleziona sesso biologico';
    }
    if (!anthro.activity_level) {
      errs.activity_level = 'Seleziona livello attività';
    }
    if (highlight && !errs[highlight]) {
      // Highlight solo il campo incriminato se è valido negli altri
    }
    setAnthroErrors(errs);
    return Object.keys(errs).length === 0;
  }

  // ─── Validazione step 3 (piano) ────────────────────────────────────────────

  const pctTotal = pctSum(planForm.protein_pct, planForm.fat_pct, planForm.carb_pct);
  const pctOk = planForm.macro_mode === 'percentage' ? Math.abs(pctTotal - 100) < 0.5 : true;
  const weeksNum = parseInt(planForm.weeks, 10);
  const weeksOk = !Number.isNaN(weeksNum) && weeksNum >= 1 && weeksNum <= 12;
  const activeMealsOk = planForm.active_meals.size > 0;

  // Override kcal: valido solo in modalità manuale, range 800-6000
  const overrideNum = parseFloat(planForm.target_kcal_override);
  const overrideOk =
    planForm.kcal_mode === 'auto' ||
    (!!planForm.target_kcal_override && !Number.isNaN(overrideNum) && overrideNum >= 800 && overrideNum <= 6000);

  const step3Valid =
    planForm.title.trim().length > 0 &&
    !!planForm.start_date &&
    weeksOk &&
    pctOk &&
    activeMealsOk &&
    overrideOk;

  // Macro input corrente (per preview + generate) — null se i campi non sono parsabili
  const currentMacroInput = useMemo<MacroInput | null>(() => {
    if (planForm.macro_mode === 'percentage') {
      const p = parseFloat(planForm.protein_pct);
      const f = parseFloat(planForm.fat_pct);
      const c = parseFloat(planForm.carb_pct);
      if ([p, f, c].some((v) => Number.isNaN(v))) return null;
      return { mode: 'percentage', protein_pct: p, fat_pct: f, carb_pct: c };
    }
    const pk = parseFloat(planForm.protein_g_per_kg);
    const fk = parseFloat(planForm.fat_g_per_kg);
    if ([pk, fk].some((v) => Number.isNaN(v))) return null;
    return { mode: 'grams_per_kg', protein_g_per_kg: pk, fat_g_per_kg: fk };
  }, [
    planForm.macro_mode,
    planForm.protein_pct,
    planForm.fat_pct,
    planForm.carb_pct,
    planForm.protein_g_per_kg,
    planForm.fat_g_per_kg,
  ]);

  // Override numerico da inviare (undefined se auto o non valido)
  const overrideToSend: number | undefined =
    planForm.kcal_mode === 'manual' && overrideOk ? overrideNum : undefined;

  // Deficit/surplus manuale da inviare — solo in modalità auto, valore valido nel range.
  // Bounds allineati al backend: kcal ±2000, percent ±40.
  const deficitNum = parseFloat(planForm.deficit_value);
  const deficitInRange =
    Number.isFinite(deficitNum) &&
    (planForm.deficit_mode === 'percent'
      ? deficitNum >= -40 && deficitNum <= 40
      : deficitNum >= -2000 && deficitNum <= 2000);
  const deficitToSend: DeficitOverride | undefined =
    planForm.kcal_mode === 'auto' && planForm.deficit_value.trim() !== '' && deficitInRange
      ? { mode: planForm.deficit_mode, value: deficitNum }
      : undefined;

  // Cambio obiettivo → aggiorna goal E prefilla il deficit col preset corrispondente
  // (nella modalità kcal/% corrente). Il pro può poi ritoccare il valore liberamente.
  const handleGoalChange = (g: GoalChoice) => {
    setGoal(g);
    setPlanForm((f) => ({ ...f, deficit_value: goalDefaultDeficit(g, f.deficit_mode) }));
  };

  // ─── 422 handler riusabile (preview + generate) ────────────────────────────
  // Torna a step 2, attiva il form, evidenzia il campo mancante.
  function handle422(e: ApiError) {
    const field = e.details as keyof UpdateAnthropometricsBody | null;
    setNeedsAnthro(true);
    setStep(2);
    if (field) {
      setAnthroErrors((prev) => ({ ...prev, [field]: 'Campo richiesto per generare il piano' }));
    }
    setError('Completa i dati clinici del paziente prima di generare il piano.');
  }

  // ─── Preview targets live (debounce ~400ms) ─────────────────────────────────
  // Attivo solo in step 3, con paziente + macro validi. Ricalcola a ogni cambio
  // di macro / override / pasti attivi.
  const activeMealsKey = useMemo(
    () => Array.from(planForm.active_meals).sort().join(','),
    [planForm.active_meals],
  );
  const previewReqId = useRef(0);

  useEffect(() => {
    if (step !== 3) return;
    if (!selectedPatientId) return;
    if (!currentMacroInput) return;
    // Se manuale ma override non valido, non chiamare (evita 400 inutili)
    if (planForm.kcal_mode === 'manual' && !overrideOk) return;

    const reqId = ++previewReqId.current;
    setPreviewLoading(true);
    setPreviewError(null);

    // Prima chiamata: NON inviare goal → il backend ritorna il goal reale del
    // profilo, con cui inizializziamo il selettore. Dopo l'init, inviamo il
    // goal scelto dall'utente così le kcal si aggiornano live.
    const isFirst = !goalInitFromPreview.current;
    const goalToSend: PatientGoalType | undefined = isFirst ? undefined : goal;

    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await previewPlanTargets({
            patient_id: selectedPatientId,
            macro_input: currentMacroInput,
            goal: goalToSend,
            target_kcal_override: overrideToSend,
            deficit_override: deficitToSend,
            active_meals: Array.from(planForm.active_meals),
          });
          if (reqId !== previewReqId.current) return; // risposta stantia
          // Inizializza il selettore obiettivo dal goal reale del profilo + prefill deficit
          if (!goalInitFromPreview.current) {
            goalInitFromPreview.current = true;
            const realGoal = normalizeGoal(res.goal);
            setGoal(realGoal);
            setPlanForm((f) =>
              f.deficit_value.trim() === ''
                ? { ...f, deficit_value: goalDefaultDeficit(realGoal, f.deficit_mode) }
                : f,
            );
          }
          setPreview(res);
          setPreviewLoading(false);
        } catch (err: unknown) {
          if (reqId !== previewReqId.current) return;
          const e = err as ApiError | Error;
          setPreviewLoading(false);
          if (e instanceof ApiError && e.status === 422) {
            handle422(e);
            return;
          }
          setPreview(null);
          setPreviewError(
            e instanceof ApiError && e.status === 400
              ? `Parametri non validi: ${e.message}`
              : 'Anteprima non disponibile.',
          );
        }
      })();
    }, 400);

    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step,
    selectedPatientId,
    currentMacroInput,
    overrideToSend,
    activeMealsKey,
    planForm.kcal_mode,
    planForm.deficit_mode,
    planForm.deficit_value,
    overrideOk,
    goal,
  ]);

  // ─── Handlers navigazione ──────────────────────────────────────────────────

  const goNext = () => {
    setError(null);
    if (step === 1) {
      if (!selectedPatientId) {
        setError('Seleziona un paziente prima di procedere');
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      // Se l'utente sta inserendo i dati clinici, salvali ORA così il preview
      // dello step 3 (che usa patient_id) li trova già persistiti. Se il save
      // fallisce, resta su step 2.
      if (needsAnthro) {
        if (!validateAnthro()) return;
        void (async () => {
          const ok = await saveAnthro();
          if (ok) setStep(3);
        })();
        return;
      }
      setStep(3);
    }
  };

  const goPrev = () => {
    setError(null);
    if (step === 2) setStep(preselectedPatientId ? 2 : 1);
    else if (step === 3) setStep(2);
  };

  // ─── Salva antropometria ────────────────────────────────────────────────────

  async function saveAnthro(): Promise<boolean> {
    if (!validateAnthro()) return false;
    try {
      await updateAnthroMutation.mutateAsync({
        patientId: selectedPatientId,
        body: {
          height_cm: parseFloat(anthro.height_cm),
          weight_kg: parseFloat(anthro.weight_kg),
          birth_date: anthro.birth_date,
          sex: anthro.sex as 'M' | 'F',
          activity_level: anthro.activity_level as ActivityLevel,
          // Migration 0039 — opzionali: stringa vuota → null (nessuna preferenza)
          cooking_simplicity: anthro.cooking_simplicity || null,
          food_preferences: anthro.food_preferences.trim() || null,
        },
      });
      return true;
    } catch (err: unknown) {
      const e = err as ApiError | Error;
      setError(`Errore salvataggio dati: ${e.message}`);
      return false;
    }
  }

  // ─── Genera piano ───────────────────────────────────────────────────────────

  const handleGenerate = () => {
    setError(null);
    if (!step3Valid) {
      setError('Compila tutti i campi richiesti prima di generare');
      return;
    }

    void (async () => {
      // L'antropometria è già stata salvata in goNext (step 2→3) se needsAnthro.
      // Save difensivo: se il preview ha rilevato un 422 e l'utente ha ricompilato,
      // ri-salviamo per sicurezza prima della generate.
      if (needsAnthro) {
        const ok = await saveAnthro();
        if (!ok) return;
      }

      if (!currentMacroInput) {
        setError('Parametri macro non validi');
        return;
      }

      const body: GeneratePlanBody = {
        patient_id: selectedPatientId,
        title: planForm.title.trim(),
        start_date: planForm.start_date,
        weeks: weeksNum,
        macro_input: currentMacroInput,
        goal,
        target_kcal_override: overrideToSend,
        deficit_override: deficitToSend,
        active_meals: Array.from(planForm.active_meals),
      };

      try {
        const res = await generateMutation.mutateAsync(body);
        setResult(res);
      } catch (err: unknown) {
        const e = err as ApiError | Error;

        // 402 — enforcement piano (quota AI mensile esaurita): modale dedicato,
        // distinto dal paywall generico (abbonamento mancante/scaduto).
        const reason = limitReason(e);
        if (reason) {
          setLimitModal(reason);
          return;
        }

        // 402 — paywall generico (abbonamento mancante/scaduto)
        if (isPaymentRequired(e)) {
          setPaywallBlocked(true);
          return;
        }

        // 422 — missing_anthropometrics: torna allo step 2 e evidenzia il campo
        if (e instanceof ApiError && e.status === 422) {
          handle422(e);
          return;
        }

        // 502 — AI upstream
        if (e instanceof ApiError && e.status === 502) {
          setError('L\'AI è temporaneamente non disponibile. Riprova tra qualche minuto.');
          return;
        }

        // 400 — macro_error / no_active_meals
        if (e instanceof ApiError && e.status === 400) {
          const detail = e.message || 'Parametri non validi';
          setError(`Errore parametri: ${detail}`);
          return;
        }

        // 404 — patient_not_found
        if (e instanceof ApiError && e.status === 404) {
          setError('Paziente non trovato. Ricarica la pagina e riprova.');
          return;
        }

        setError(e.message || String(err));
      }
    })();
  };

  const isGenerating = generateMutation.isPending || updateAnthroMutation.isPending;

  // ─── Render ────────────────────────────────────────────────────────────────

  // Quota AI esaurita: mostra il modale dedicato al posto del wizard (chiude
  // anche il wizard sottostante per evitare due overlay sovrapposti).
  if (limitModal) {
    return <LimitReachedModal reason={limitModal} onClose={onClose} />;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Genera dieta con AI"
      onClick={() => !isGenerating && !result && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1200,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 14,
          padding: 24,
          maxWidth: 560,
          width: '100%',
          boxShadow: '0 24px 60px rgba(0,0,0,0.32)',
          maxHeight: '90vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #6b46c1, #d946ef)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Sparkles size={16} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <h2
              style={{
                fontFamily: 'var(--display)',
                fontSize: 20,
                fontWeight: 800,
                margin: 0,
                letterSpacing: -0.5,
              }}
            >
              Genera dieta con AI
            </h2>
            {!result && (
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: 'var(--mute)',
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  marginTop: 2,
                }}
              >
                Piano personalizzato
              </div>
            )}
          </div>
          {!isGenerating && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Chiudi"
              style={{
                border: '1px solid var(--line)',
                background: 'var(--surface)',
                color: 'var(--ink-2)',
                width: 30,
                height: 30,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Paywall */}
        {paywallBlocked ? (
          <PaywallNotice resource="la generazione AI del piano" />
        ) : result ? (
          /* ── Risultato ── */
          <SuccessPanel
            result={result}
            patientName={selectedPatient?.name || ''}
            onOpenPlan={() => {
              onClose();
              navigate(`/piani/${result.plan_id}/edit`);
            }}
            onClose={onClose}
          />
        ) : (
          <>
            {/* Step indicator */}
            {!isGenerating && (
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  marginBottom: 18,
                  padding: 3,
                  background: 'var(--surface-2)',
                  borderRadius: 8,
                }}
              >
                {STEPS.map((s) => {
                  if (preselectedPatientId && s.n === 1) return null;
                  const active = step === s.n;
                  const done = step > s.n;
                  return (
                    <div
                      key={s.n}
                      style={{
                        flex: 1,
                        padding: '8px 10px',
                        background: active ? 'var(--surface)' : 'transparent',
                        borderRadius: 6,
                        boxShadow: active ? 'var(--shadow-sm)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 7,
                      }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 5,
                          background: done
                            ? 'var(--green-deep)'
                            : active
                            ? 'linear-gradient(135deg, #6b46c1, #d946ef)'
                            : 'var(--line)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: 'var(--mono)',
                          fontSize: 10,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {done ? <Check size={11} /> : s.n}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: active ? 'var(--ink)' : done ? 'var(--green-deep)' : 'var(--mute)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {s.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Errore */}
            {error && !isGenerating && (
              <div
                role="alert"
                style={{
                  background: 'var(--red-soft, #fff0f0)',
                  border: '1px solid var(--red)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontSize: 13,
                  color: 'var(--red)',
                  marginBottom: 14,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                <AlertTriangle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
                <span>{error}</span>
              </div>
            )}

            {/* Stato generazione in corso */}
            {isGenerating ? (
              <GeneratingState />
            ) : step === 1 ? (
              <StepPaziente
                patients={filteredPatients}
                patientsLoading={patientsLoading}
                selectedId={selectedPatientId}
                searchQ={searchQ}
                onSearchChange={setSearchQ}
                onSelect={setSelectedPatientId}
              />
            ) : step === 2 ? (
              <StepAnthro
                anthro={anthro}
                errors={anthroErrors}
                needsAnthro={needsAnthro}
                patientName={selectedPatient?.name}
                onChange={setAnthro}
                onToggleNeedsAnthro={setNeedsAnthro}
              />
            ) : (
              <StepPiano
                form={planForm}
                pctTotal={pctTotal}
                pctOk={pctOk}
                weeksOk={weeksOk}
                onChange={setPlanForm}
                goal={goal}
                onGoalChange={handleGoalChange}
                preview={preview}
                previewLoading={previewLoading}
                previewError={previewError}
                overrideOk={overrideOk}
              />
            )}

            {/* Footer nav */}
            {!isGenerating && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: '1px solid var(--line-3)',
                  flexWrap: 'wrap',
                }}
              >
                {step > 1 && !preselectedPatientId ? (
                  <button
                    type="button"
                    onClick={goPrev}
                    style={{
                      border: '1px solid var(--line)',
                      background: 'var(--surface)',
                      color: 'var(--ink)',
                      padding: '9px 14px',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                    }}
                  >
                    <ChevronLeft size={14} /> Indietro
                  </button>
                ) : step === 3 ? (
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    style={{
                      border: '1px solid var(--line)',
                      background: 'var(--surface)',
                      color: 'var(--ink)',
                      padding: '9px 14px',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                    }}
                  >
                    <ChevronLeft size={14} /> Indietro
                  </button>
                ) : null}

                <div style={{ flex: 1 }} />

                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    border: '1px solid var(--line)',
                    background: 'transparent',
                    color: 'var(--ink-2)',
                    padding: '9px 14px',
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  Annulla
                </button>

                {step < 3 ? (
                  <button
                    type="button"
                    onClick={goNext}
                    style={{
                      border: 0,
                      background: 'var(--ink)',
                      color: '#fff',
                      padding: '9px 18px',
                      borderRadius: 8,
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                    }}
                  >
                    Avanti <ChevronRight size={14} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={!step3Valid}
                    style={{
                      border: 0,
                      background: step3Valid
                        ? 'linear-gradient(135deg, #6b46c1, #d946ef)'
                        : 'var(--line)',
                      color: '#fff',
                      padding: '9px 18px',
                      borderRadius: 8,
                      fontWeight: 700,
                      cursor: step3Valid ? 'pointer' : 'not-allowed',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                    }}
                  >
                    <Sparkles size={14} /> Genera piano
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Step 1: Selezione paziente ───────────────────────────────────────────────

function StepPaziente({
  patients,
  patientsLoading,
  selectedId,
  searchQ,
  onSearchChange,
  onSelect,
}: {
  patients: Array<{ id: string; name: string; human_id: string | null; email: string | null }>;
  patientsLoading: boolean;
  selectedId: string;
  searchQ: string;
  onSearchChange: (q: string) => void;
  onSelect: (id: string) => void;
}) {
  return (
    <div>
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--mute)',
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        Seleziona il paziente per cui generare il piano
      </div>

      <input
        type="text"
        value={searchQ}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Cerca per nome, PZ-ID, email…"
        style={{ ...inputStyle, marginBottom: 8 }}
        autoFocus
      />

      <div
        style={{
          maxHeight: 260,
          overflowY: 'auto',
          border: '1px solid var(--line)',
          borderRadius: 8,
          background: 'var(--surface)',
        }}
      >
        {patientsLoading ? (
          <div style={{ padding: 14, fontSize: 13, color: 'var(--mute)' }}>
            Caricamento pazienti…
          </div>
        ) : patients.length === 0 ? (
          <div style={{ padding: 14, fontSize: 13, color: 'var(--mute)' }}>
            Nessun paziente trovato.
          </div>
        ) : (
          patients.map((p) => {
            const active = selectedId === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onSelect(p.id)}
                style={{
                  display: 'flex',
                  width: '100%',
                  padding: '10px 12px',
                  border: 0,
                  borderBottom: '1px solid var(--line-3)',
                  background: active ? 'var(--green-soft)' : 'var(--surface)',
                  cursor: 'pointer',
                  alignItems: 'center',
                  gap: 10,
                  textAlign: 'left',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                    {p.name}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--mute)',
                      fontFamily: 'var(--mono)',
                      marginTop: 2,
                    }}
                  >
                    {p.human_id || '—'}
                    {p.email ? ` · ${p.email}` : ''}
                  </div>
                </div>
                {active && <Check size={14} color="var(--green-deep)" />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Step 2: Dati antropometrici ─────────────────────────────────────────────

function StepAnthro({
  anthro,
  errors,
  needsAnthro,
  patientName,
  onChange,
  onToggleNeedsAnthro,
}: {
  anthro: AnthroForm;
  errors: Partial<Record<keyof AnthroForm, string>>;
  needsAnthro: boolean;
  patientName?: string;
  onChange: (f: AnthroForm) => void;
  onToggleNeedsAnthro: (v: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55 }}>
        Il motore AI usa altezza, età, sesso e livello di attività per calcolare il fabbisogno
        calorico di <strong>{patientName || 'questo paziente'}</strong>.
      </div>

      {/* Toggle "dati già nel profilo / inserisci ora" */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          background: 'var(--surface-2)',
          borderRadius: 8,
          border: '1px solid var(--line)',
        }}
      >
        <input
          id="needs-anthro-toggle"
          type="checkbox"
          checked={needsAnthro}
          onChange={(e) => onToggleNeedsAnthro(e.target.checked)}
          style={{ width: 16, height: 16, cursor: 'pointer' }}
        />
        <label
          htmlFor="needs-anthro-toggle"
          style={{ fontSize: 13, color: 'var(--ink)', cursor: 'pointer', lineHeight: 1.4 }}
        >
          Inserisci / aggiorna i dati clinici ora
          <span style={{ display: 'block', fontSize: 11, color: 'var(--mute)', marginTop: 2 }}>
            Deseleziona se i dati sono già nel profilo paziente
          </span>
        </label>
      </div>

      {needsAnthro && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
          }}
        >
          <FieldBlock label="Altezza (cm)" error={errors.height_cm}>
            <input
              type="number"
              min={100}
              max={250}
              value={anthro.height_cm}
              onChange={(e) => onChange({ ...anthro, height_cm: e.target.value })}
              placeholder="es. 170"
              style={errors.height_cm ? inputErrorStyle : inputStyle}
            />
          </FieldBlock>

          <FieldBlock label="Peso (kg)" error={errors.weight_kg}>
            <input
              type="number"
              min={30}
              max={300}
              step={0.1}
              value={anthro.weight_kg}
              onChange={(e) => onChange({ ...anthro, weight_kg: e.target.value })}
              placeholder="es. 70"
              style={errors.weight_kg ? inputErrorStyle : inputStyle}
            />
          </FieldBlock>

          <FieldBlock label="Data di nascita" error={errors.birth_date}>
            <input
              type="date"
              value={anthro.birth_date}
              onChange={(e) => onChange({ ...anthro, birth_date: e.target.value })}
              max={new Date().toISOString().split('T')[0]}
              style={errors.birth_date ? inputErrorStyle : inputStyle}
            />
          </FieldBlock>

          <FieldBlock label="Sesso biologico" error={errors.sex}>
            <select
              value={anthro.sex}
              onChange={(e) => onChange({ ...anthro, sex: e.target.value as 'M' | 'F' | '' })}
              style={errors.sex ? inputErrorStyle : inputStyle}
            >
              <option value="">Seleziona…</option>
              <option value="M">Maschio (M)</option>
              <option value="F">Femmina (F)</option>
            </select>
          </FieldBlock>

          <FieldBlock label="Livello attività" error={errors.activity_level}>
            <select
              value={anthro.activity_level}
              onChange={(e) =>
                onChange({ ...anthro, activity_level: e.target.value as ActivityLevel | '' })
              }
              style={errors.activity_level ? inputErrorStyle : inputStyle}
            >
              <option value="">Seleziona…</option>
              {ACTIVITY_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {ACTIVITY_LABELS[k]}
                </option>
              ))}
            </select>
          </FieldBlock>
        </div>
      )}

      {needsAnthro && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: '.5px',
              textTransform: 'uppercase',
              color: 'var(--mute)',
            }}
          >
            Abitudini e preferenze
          </div>

          <FieldBlock label="Come cucina il paziente">
            <select
              value={anthro.cooking_simplicity}
              onChange={(e) =>
                onChange({ ...anthro, cooking_simplicity: e.target.value as CookingSimplicity | '' })
              }
              style={inputStyle}
            >
              <option value="">Non specificato</option>
              {SIMPLICITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label} — {o.hint}
                </option>
              ))}
            </select>
          </FieldBlock>

          <FieldBlock label="Preferenze alimentari (gusti, cibi non graditi)">
            <textarea
              value={anthro.food_preferences}
              onChange={(e) => onChange({ ...anthro, food_preferences: e.target.value })}
              placeholder="es. non gradisce il pesce, ama la pasta, poca carne rossa"
              maxLength={500}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </FieldBlock>
        </div>
      )}

      {!needsAnthro && (
        <div
          style={{
            padding: '10px 12px',
            background: 'var(--green-soft, #e8f5ee)',
            border: '1px solid var(--green-deep)',
            borderRadius: 8,
            fontSize: 13,
            color: 'var(--green-deep)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Check size={14} />
          Verranno usati i dati clinici già presenti nel profilo del paziente.
          Se mancano, il backend richiederà di inserirli.
        </div>
      )}
    </div>
  );
}

// ─── Step 3: Parametri piano ─────────────────────────────────────────────────

function StepPiano({
  form,
  pctTotal,
  pctOk,
  weeksOk,
  onChange,
  goal,
  onGoalChange,
  preview,
  previewLoading,
  previewError,
  overrideOk,
}: {
  form: PlanForm;
  pctTotal: number;
  pctOk: boolean;
  weeksOk: boolean;
  onChange: (f: PlanForm) => void;
  goal: GoalChoice;
  onGoalChange: (g: GoalChoice) => void;
  preview: PreviewTargetsResponse | null;
  previewLoading: boolean;
  previewError: string | null;
  overrideOk: boolean;
}) {
  const manual = form.kcal_mode === 'manual';

  // Cambio modalità deficit (kcal ↔ %): converte il valore corrente usando il TDEE del
  // preview se disponibile, altrimenti reimposta al preset dell'obiettivo nella nuova modalità.
  const setDeficitMode = (mode: 'kcal' | 'percent') => {
    if (mode === form.deficit_mode) return;
    const tdee = preview?.tdee;
    const cur = parseFloat(form.deficit_value);
    let nextVal: string;
    if (Number.isFinite(cur) && tdee && tdee > 0) {
      nextVal =
        mode === 'percent'
          ? String(Math.round((cur / tdee) * 100))
          : String(Math.round((tdee * cur) / 100));
    } else {
      nextVal = goalDefaultDeficit(goal, mode);
    }
    onChange({ ...form, deficit_mode: mode, deficit_value: nextVal });
  };

  const toggleMeal = (mt: MealType) => {
    const next = new Set(form.active_meals);
    if (next.has(mt)) {
      if (next.size <= 1) return; // almeno 1 pasto
      next.delete(mt);
    } else {
      next.add(mt);
    }
    onChange({ ...form, active_meals: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Titolo + date + settimane */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        <div style={{ gridColumn: '1 / -1' }}>
          <FieldBlock label="Titolo piano">
            <input
              type="text"
              value={form.title}
              onChange={(e) => onChange({ ...form, title: e.target.value })}
              maxLength={120}
              placeholder="es. Piano Mediterraneo Giugno 2026"
              style={inputStyle}
            />
          </FieldBlock>
        </div>

        <FieldBlock label="Data inizio">
          <input
            type="date"
            value={form.start_date}
            onChange={(e) => onChange({ ...form, start_date: e.target.value })}
            style={inputStyle}
          />
        </FieldBlock>

        <FieldBlock label="Durata (settimane)">
          <input
            type="number"
            min={1}
            max={12}
            value={form.weeks}
            onChange={(e) => onChange({ ...form, weeks: e.target.value })}
            style={!weeksOk ? inputErrorStyle : inputStyle}
          />
          {!weeksOk && (
            <span style={{ fontSize: 11.5, color: 'var(--red)' }}>Valore 1-12</span>
          )}
        </FieldBlock>
      </div>

      {/* Toggle mode macro */}
      <div>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 700,
            color: 'var(--mute)',
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          Impostazione macronutrienti
        </div>
        <div
          style={{
            display: 'flex',
            gap: 6,
            padding: 3,
            background: 'var(--surface-2)',
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {(['percentage', 'grams_per_kg'] as const).map((m) => {
            const active = form.macro_mode === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => onChange({ ...form, macro_mode: m })}
                style={{
                  flex: 1,
                  padding: '7px 10px',
                  border: 0,
                  background: active ? 'var(--surface)' : 'transparent',
                  borderRadius: 6,
                  fontWeight: active ? 700 : 500,
                  fontSize: 12.5,
                  color: active ? 'var(--ink)' : 'var(--mute)',
                  cursor: 'pointer',
                  boxShadow: active ? 'var(--shadow-sm)' : 'none',
                }}
              >
                {m === 'percentage' ? '% Percentuali' : 'g/kg Grammi per kg'}
              </button>
            );
          })}
        </div>

        {form.macro_mode === 'percentage' ? (
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
              }}
            >
              <FieldBlock label="Proteine %">
                <input
                  type="number"
                  min={5}
                  max={70}
                  value={form.protein_pct}
                  onChange={(e) => onChange({ ...form, protein_pct: e.target.value })}
                  style={inputStyle}
                />
              </FieldBlock>
              <FieldBlock label="Grassi %">
                <input
                  type="number"
                  min={5}
                  max={70}
                  value={form.fat_pct}
                  onChange={(e) => onChange({ ...form, fat_pct: e.target.value })}
                  style={inputStyle}
                />
              </FieldBlock>
              <FieldBlock label="Carboidrati %">
                <input
                  type="number"
                  min={5}
                  max={85}
                  value={form.carb_pct}
                  onChange={(e) => onChange({ ...form, carb_pct: e.target.value })}
                  style={inputStyle}
                />
              </FieldBlock>
            </div>
            {/* Totale live */}
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: pctOk ? 'var(--green-deep)' : 'var(--red)',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              {pctOk ? <Check size={12} /> : <AlertTriangle size={12} />}
              Totale: {pctTotal.toFixed(0)}%
              {!pctOk && ' — deve sommare esattamente 100%'}
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <FieldBlock label="Proteine (g/kg)">
              <input
                type="number"
                min={0.5}
                max={4}
                step={0.1}
                value={form.protein_g_per_kg}
                onChange={(e) => onChange({ ...form, protein_g_per_kg: e.target.value })}
                style={inputStyle}
              />
            </FieldBlock>
            <FieldBlock label="Grassi (g/kg)">
              <input
                type="number"
                min={0.3}
                max={3}
                step={0.1}
                value={form.fat_g_per_kg}
                onChange={(e) => onChange({ ...form, fat_g_per_kg: e.target.value })}
                style={inputStyle}
              />
            </FieldBlock>
            <div
              style={{
                gridColumn: '1 / -1',
                fontSize: 12,
                color: 'var(--mute)',
                fontStyle: 'italic',
              }}
            >
              I carboidrati vengono calcolati automaticamente a saldo sul fabbisogno energetico totale.
            </div>
          </div>
        )}
      </div>

      {/* Obiettivo */}
      <div>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 700,
            color: 'var(--mute)',
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          Obiettivo
        </div>
        <div
          style={{
            display: 'flex',
            gap: 6,
            opacity: manual ? 0.5 : 1,
          }}
        >
          {GOAL_OPTIONS.map((g) => {
            const active = goal === g.value;
            return (
              <button
                key={g.value}
                type="button"
                disabled={manual}
                onClick={() => onGoalChange(g.value)}
                style={{
                  flex: 1,
                  padding: '9px 8px',
                  border: `1px solid ${active && !manual ? 'var(--ink)' : 'var(--line)'}`,
                  background: active && !manual ? 'var(--ink)' : 'var(--surface)',
                  color: active && !manual ? '#fff' : 'var(--ink-2)',
                  borderRadius: 8,
                  fontWeight: active ? 700 : 500,
                  fontSize: 12.5,
                  cursor: manual ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <span>{g.label}</span>
                <span
                  style={{
                    fontSize: 10,
                    fontFamily: 'var(--mono)',
                    opacity: 0.8,
                  }}
                >
                  {g.hint}
                </span>
              </button>
            );
          })}
        </div>
        {manual && (
          <div style={{ fontSize: 11.5, color: 'var(--mute)', marginTop: 6, fontStyle: 'italic' }}>
            Calorie manuali attive: l'obiettivo è ignorato (vince il valore impostato).
          </div>
        )}
      </div>

      {/* Deficit / Surplus calorico (modalità automatica): kcal o % del TDEE, editabile */}
      {!manual && (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                fontWeight: 700,
                color: 'var(--mute)',
                letterSpacing: 1.2,
                textTransform: 'uppercase',
              }}
            >
              Deficit / Surplus
            </span>
            <div style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--surface-2)', borderRadius: 8 }}>
              {(['kcal', 'percent'] as const).map((md) => {
                const active = form.deficit_mode === md;
                return (
                  <button
                    key={md}
                    type="button"
                    onClick={() => setDeficitMode(md)}
                    style={{
                      padding: '5px 12px',
                      border: 0,
                      background: active ? 'var(--surface)' : 'transparent',
                      borderRadius: 6,
                      fontWeight: active ? 700 : 500,
                      fontSize: 12,
                      color: active ? 'var(--ink)' : 'var(--mute)',
                      cursor: 'pointer',
                      boxShadow: active ? 'var(--shadow-sm)' : 'none',
                    }}
                  >
                    {md === 'kcal' ? 'kcal' : '% del TDEE'}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <input
              type="number"
              step={form.deficit_mode === 'percent' ? 1 : 50}
              value={form.deficit_value}
              onChange={(e) => onChange({ ...form, deficit_value: e.target.value })}
              placeholder={form.deficit_mode === 'percent' ? 'es. −20' : 'es. −500'}
              aria-label="Deficit o surplus calorico"
              style={{ ...inputStyle, paddingRight: 64 }}
            />
            <span
              style={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                fontFamily: 'var(--mono)',
                fontSize: 11,
                color: 'var(--mute)',
                pointerEvents: 'none',
              }}
            >
              {form.deficit_mode === 'percent' ? '% TDEE' : 'kcal/g'}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--mute)', marginTop: 6, lineHeight: 1.5 }}>
            Negativo = deficit (dimagrimento), positivo = surplus (massa). Gli obiettivi qui sopra
            sono preset: ritocca il valore liberamente. Il floor di sicurezza (1200 kcal donna /
            1500 kcal uomo) resta sempre garantito.
          </div>
        </div>
      )}

      {/* Calorie: automatiche / manuali */}
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 8,
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10.5,
              fontWeight: 700,
              color: 'var(--mute)',
              letterSpacing: 1.2,
              textTransform: 'uppercase',
            }}
          >
            Calorie target
          </span>
          <div
            style={{
              display: 'flex',
              gap: 4,
              padding: 3,
              background: 'var(--surface-2)',
              borderRadius: 8,
            }}
          >
            {(['auto', 'manual'] as const).map((m) => {
              const active = form.kcal_mode === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => onChange({ ...form, kcal_mode: m })}
                  style={{
                    padding: '6px 12px',
                    border: 0,
                    background: active ? 'var(--surface)' : 'transparent',
                    borderRadius: 6,
                    fontWeight: active ? 700 : 500,
                    fontSize: 12,
                    color: active ? 'var(--ink)' : 'var(--mute)',
                    cursor: 'pointer',
                    boxShadow: active ? 'var(--shadow-sm)' : 'none',
                  }}
                >
                  {m === 'auto' ? 'Automatiche' : 'Manuali'}
                </button>
              );
            })}
          </div>
        </div>

        {manual && (
          <div style={{ marginBottom: 12 }}>
            <FieldBlock label="Calorie/giorno (800-6000)">
              <input
                type="number"
                min={800}
                max={6000}
                step={50}
                value={form.target_kcal_override}
                onChange={(e) => onChange({ ...form, target_kcal_override: e.target.value })}
                placeholder="es. 2000"
                style={!overrideOk ? inputErrorStyle : inputStyle}
              />
              {!overrideOk && (
                <span style={{ fontSize: 11.5, color: 'var(--red)' }}>
                  Valore richiesto, range 800-6000 kcal
                </span>
              )}
            </FieldBlock>
          </div>
        )}

        {/* Pannello kcal/macro live */}
        <KcalPanel
          preview={preview}
          loading={previewLoading}
          error={previewError}
          manualValid={!manual || overrideOk}
        />
      </div>

      {/* Pasti attivi */}
      <div>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 700,
            color: 'var(--mute)',
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          Pasti da includere
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {MEAL_TYPES.map(({ value, label }) => {
            const active = form.active_meals.has(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleMeal(value)}
                style={{
                  padding: '6px 12px',
                  border: `1px solid ${active ? 'var(--green-deep)' : 'var(--line)'}`,
                  background: active ? 'var(--green-soft, #e8f5ee)' : 'var(--surface)',
                  color: active ? 'var(--green-deep)' : 'var(--ink-2)',
                  borderRadius: 6,
                  fontWeight: active ? 700 : 500,
                  fontSize: 12.5,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                {active && <Check size={11} />}
                {label}
              </button>
            );
          })}
        </div>
        {form.active_meals.size === 0 && (
          <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 6 }}>
            Seleziona almeno un pasto.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Pannello kcal/macro live (preview-targets) ──────────────────────────────

function KcalPanel({
  preview,
  loading,
  error,
  manualValid,
}: {
  preview: PreviewTargetsResponse | null;
  loading: boolean;
  error: string | null;
  manualValid: boolean;
}) {
  // Se l'override manuale non è valido, non mostriamo numeri stantii: chiediamo input
  if (!manualValid) {
    return (
      <div
        style={{
          padding: '12px 14px',
          background: 'var(--surface-2)',
          border: '1px dashed var(--line)',
          borderRadius: 10,
          fontSize: 12.5,
          color: 'var(--mute)',
        }}
      >
        Inserisci un valore calorico valido per vedere l'anteprima.
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '12px 14px',
          background: 'var(--orange-soft, #fff3d6)',
          border: '1px solid var(--orange)',
          borderRadius: 10,
          fontSize: 12.5,
          color: 'var(--orange-deep, #a87c00)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <AlertTriangle size={14} /> {error}
      </div>
    );
  }

  // Stato vuoto / primo caricamento
  if (!preview) {
    return (
      <div
        style={{
          padding: '14px',
          background: 'var(--surface-2)',
          border: '1px solid var(--line)',
          borderRadius: 10,
          fontSize: 12.5,
          color: 'var(--mute)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
        Calcolo del fabbisogno in corso…
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const sourceLabel =
    preview.kcal_source === 'override'
      ? 'impostate manualmente'
      : preview.kcal_source === 'deficit'
        ? 'deficit/surplus su misura'
        : 'calcolate dal motore';

  return (
    <div
      style={{
        padding: '14px 16px',
        background: 'linear-gradient(135deg, rgba(107,70,193,0.08), rgba(217,70,239,0.08))',
        border: '1px solid var(--line)',
        borderRadius: 12,
        position: 'relative',
        opacity: loading ? 0.7 : 1,
        transition: 'opacity 0.15s',
      }}
    >
      {/* kcal headline */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #6b46c1, #d946ef)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Flame size={15} color="#fff" />
        </div>
        <div>
          <div
            style={{
              fontFamily: 'var(--display)',
              fontSize: 20,
              fontWeight: 800,
              color: 'var(--ink)',
              letterSpacing: -0.5,
              lineHeight: 1.1,
            }}
          >
            Fabbisogno stimato: {preview.target_kcal} kcal/giorno
          </div>
          <div style={{ fontSize: 11, color: 'var(--mute)', marginTop: 2 }}>
            {sourceLabel}
            {loading && ' · aggiornamento…'}
          </div>
        </div>
      </div>

      {/* macro grammi */}
      <div
        style={{
          fontSize: 13.5,
          fontWeight: 700,
          color: 'var(--ink)',
          marginBottom: 8,
          fontFamily: 'var(--mono)',
        }}
      >
        P {preview.macros.protein_g}g · C {preview.macros.carb_g}g · G {preview.macros.fat_g}g
      </div>

      {/* riga bmr/tdee/obiettivo */}
      <div
        style={{
          fontSize: 11,
          color: 'var(--mute)',
          fontFamily: 'var(--mono)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <span>BMR {preview.bmr}</span>
        <span>TDEE {preview.tdee}</span>
        <span>Obiettivo: {GOAL_OPTIONS.find((o) => o.value === preview.goal)?.label ?? 'Mantenimento'}</span>
        {preview.delta_kcal !== 0 && (
          <span style={{ color: preview.delta_kcal < 0 ? 'var(--green-deep)' : 'var(--orange-deep, #a87c00)' }}>
            {preview.delta_kcal > 0 ? '+' : ''}
            {preview.delta_kcal} kcal vs TDEE
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Stato generazione in corso ───────────────────────────────────────────────

function GeneratingState() {
  const [dots, setDots] = useState('');
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    let n = 0;
    const dotsT = setInterval(() => {
      n = (n + 1) % 4;
      setDots('.'.repeat(n));
    }, 500);
    const start = Date.now();
    const elapsedT = setInterval(() => setElapsedMs(Date.now() - start), 100);
    return () => {
      clearInterval(dotsT);
      clearInterval(elapsedT);
    };
  }, []);
  const totalSec = Math.floor(elapsedMs / 1000);
  const mm = String(Math.floor(totalSec / 60)).padStart(2, '0');
  const ss = String(totalSec % 60).padStart(2, '0');

  return (
    <div
      style={{
        padding: '32px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 12,
          background: 'linear-gradient(135deg, #6b46c1, #d946ef)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loader2 size={26} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
      {/* Cronometro reale: tempo effettivo trascorso, non una stima marketing. */}
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 30,
          fontWeight: 700,
          color: 'var(--ink)',
          letterSpacing: 1,
          fontVariantNumeric: 'tabular-nums',
        }}
        aria-live="polite"
        aria-label={`Tempo trascorso ${mm} minuti ${ss} secondi`}
      >
        {mm}:{ss}
      </div>
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontFamily: 'var(--display)',
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--ink)',
          }}
        >
          Sto generando il piano{dots}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--mute)', marginTop: 6, lineHeight: 1.5 }}>
          L'AI sta costruendo i pasti personalizzati giorno per giorno.
          <br />
          Non chiudere questa finestra.
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Pannello successo ────────────────────────────────────────────────────────

function SuccessPanel({
  result,
  patientName,
  onOpenPlan,
  onClose,
}: {
  result: GenerateResult;
  patientName: string;
  onOpenPlan: () => void;
  onClose: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header success */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          background: 'var(--green-soft, #e8f5ee)',
          border: '1px solid var(--green-deep)',
          borderRadius: 10,
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: 'var(--green-deep)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Check size={16} color="#fff" />
        </div>
        <div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--green-deep)',
            }}
          >
            Piano generato con successo!
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>
            Per {patientName || 'il paziente'} ·{' '}
            {result.meals_created} pasti · {result.items_created} ingredienti
          </div>
        </div>
      </div>

      {/* Warnings */}
      {result.warnings && result.warnings.length > 0 && (
        <div
          role="alert"
          style={{
            background: 'var(--orange-soft, #fff3d6)',
            border: '1px solid var(--orange)',
            borderRadius: 8,
            padding: '10px 12px',
            fontSize: 12.5,
            color: 'var(--orange-deep, #a87c00)',
          }}
        >
          <div
            style={{
              fontWeight: 700,
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <AlertTriangle size={13} /> Note AI ({result.warnings.length})
          </div>
          <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.6 }}>
            {result.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Riga kcal/giorno del piano generato */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '11px 14px',
          background: 'linear-gradient(135deg, rgba(107,70,193,0.08), rgba(217,70,239,0.08))',
          border: '1px solid var(--line)',
          borderRadius: 10,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: 'linear-gradient(135deg, #6b46c1, #d946ef)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Flame size={14} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: 'var(--display)',
              fontSize: 17,
              fontWeight: 800,
              color: 'var(--ink)',
              letterSpacing: -0.4,
            }}
          >
            {result.target_kcal} kcal/giorno
          </div>
          <div style={{ fontSize: 11, color: 'var(--mute)', fontFamily: 'var(--mono)', marginTop: 1 }}>
            P {result.macros.protein_g}g · C {result.macros.carb_g}g · G {result.macros.fat_g}g
          </div>
        </div>
      </div>

      {/* KPI riepilogo */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 8,
        }}
      >
        {[
          { label: 'Pasti creati', value: String(result.meals_created) },
          { label: 'Ingredienti totali', value: String(result.items_created) },
        ].map(({ label, value }) => (
          <div
            key={label}
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--line)',
              borderRadius: 8,
              padding: '10px 12px',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--mute)',
                letterSpacing: 1.2,
                textTransform: 'uppercase',
              }}
            >
              {label}
            </div>
            <div
              style={{
                fontFamily: 'var(--display)',
                fontSize: 22,
                fontWeight: 800,
                color: 'var(--ink)',
                marginTop: 3,
                letterSpacing: -0.5,
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            color: 'var(--ink-2)',
            padding: '9px 14px',
            borderRadius: 8,
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          Chiudi
        </button>
        <button
          type="button"
          onClick={onOpenPlan}
          style={{
            border: 0,
            background: 'var(--green-deep)',
            color: '#fff',
            padding: '9px 18px',
            borderRadius: 8,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
          }}
        >
          Apri il piano <ExternalLink size={13} />
        </button>
      </div>
    </div>
  );
}
